import { useDispatch, useSelector } from 'react-redux'
import DelayClock from "../components/DelayClock";
import { selectPaymentBtnText, selectI18nPaymentBtnText, selectBackBtnText, selectI18nBackBtnText, selectFirstView, selectFormValid, paymentMethodSlice, selectLoading as selectPaymentMethodLoading, selectNextAction, selectId as selectPaymentMethodId } from '../app/reducers/paymentMethod'
import { operationSlice } from '../app/reducers/operation'
import { selectIsTicking, setIsTicking, selectDelay, selectTimeLeft, setTimeLeft, selectSkipDelay, setSkipDelay } from '../app/reducers/delayClock'
import { useExecutePaymentMethodMutation } from '../app/services/paymentMethod'
import { selectList } from '../app/reducers/methodsList'
import { selectIntegrations } from '../app/reducers/integrations'
import { selectLanguage } from '../app/reducers/language'
import { selectSettings as selectConfigSettings } from '../app/reducers/flexConfiguration'
import OccurrenceRegistry from '../components/OccurrenceRegistry'
import { useTranslation } from '../hooks/useTranslation'
import { useRef } from 'react'
import GooglePayButton from '../components/GooglePayButton'
import { useGooglePayFlow } from '../hooks/useGooglePayFlow'
import { getCurrencySymbolSafe, formatAmountPart } from '../helpers/currency'

export default function PaymentActions({ chooseMethod, responseCallback, showResult }) {
    const popupRef = useRef(null);
    const returnFiredRef = useRef(false);
    const dispatch = useDispatch();
    const operation = useSelector(operationSlice.selectSlice);
    const paymentMethod = useSelector(paymentMethodSlice.selectSlice);
    const nextAction = useSelector(selectNextAction);
    const paymentMethodLoading = useSelector(selectPaymentMethodLoading);
    const [executePaymentMethod] = useExecutePaymentMethodMutation();
    const paymentBtnText = useSelector(selectPaymentBtnText);
    const i18nPaymentBtnText = useSelector(selectI18nPaymentBtnText);
    const backBtnText = useSelector(selectBackBtnText);
    const i18nBackBtnText = useSelector(selectI18nBackBtnText);
    const isFirstView = useSelector(selectFirstView);
    const isFormValid = useSelector(selectFormValid);
    const delay = useSelector(selectDelay);
    const isTicking = useSelector(selectIsTicking);
    const timeLeft = useSelector(selectTimeLeft);
    const skipDelay = useSelector(selectSkipDelay);
    const configSettings = useSelector(selectConfigSettings);
    const methodsList = useSelector(selectList);
    const methodId = useSelector(selectPaymentMethodId);
    const integrations = useSelector(selectIntegrations);
    const language = useSelector(selectLanguage);
    const { t, tMixed, tBackend } = useTranslation();

    const onStartOfDelay = () => { dispatch(setIsTicking(true)) };
    const onDelayTick = (count) => { dispatch(setTimeLeft(count)) };
    const onEndOfDelay = () => { dispatch(setIsTicking(false)); onSubmit() };

    // Método seleccionado actual (derivado del store, no de props)
    const selectedMethod = methodsList.find((m) => m.id === methodId);

    // Flujo Google Pay (dos pasos: boton nativo -> seleccion de cuotas si es credito).
    // El estado vive aqui para pintar el contenido (arriba) y el boton (barra inferior) en zonas separadas.
    const googlePay = useGooglePayFlow({ operation, onSubmit: (override) => onSubmit(override) });

    // Progreso de la barra de resultado (sin timer nuevo; deriva del estado existente)
    const progress = (delay && delay > 0) ? ((delay - timeLeft) / delay) * 100 : 0;

    // "Volver ahora": acelera la redirección reutilizando setSkipDelay(true), como maximo una vez
    // y solo si la redireccion no ocurrio ni esta en proceso.
    const onReturnNow = () => {
      if (returnFiredRef.current) return;   // ya se disparo en este montaje
      if (skipDelay) return;                // el salto ya fue disparado (por boton o flujo)
      if (timeLeft <= 0) return;            // el countdown ya termino -> redireccion ya ocurrio
      returnFiredRef.current = true;
      dispatch(setSkipDelay(true));
    };

    // Monto/simbolo: helpers unificados de helpers/currency (variantes seguras para UI).
    // Acceso defensivo: operation.operation puede no estar disponible en todos los renders
    // (p.ej. antes de cargar la operacion). getCurrencySymbolSafe tolera undefined -> "".
    const paymentDetails = (operation && operation.operation && operation.operation.payment_details)
      ? operation.operation.payment_details
      : null;
    const currencyCode = paymentDetails ? paymentDetails.currency : null;
    const amountMinor = paymentDetails ? paymentDetails.amount : null;

    async function onSubmit(paymentMethodOverride) {
      // paymentMethodOverride (opcional): permite pasar el paymentMethod ya actualizado
      // (p.ej. Google Pay con field_google_pay_token) sin depender del re-render de Redux.
      // Los metodos existentes siguen llamando onSubmit() sin argumentos.
      const paymentMethodToSubmit = (paymentMethodOverride && typeof paymentMethodOverride === 'object')
        ? paymentMethodOverride
        : paymentMethod;
      if(paymentMethodLoading){
        return;
      }
      if(isFirstView && !isFormValid){
        return;
      }
      if(nextAction!=undefined){
        if(nextAction.type=="open_popup"){
          return;
        }
        if(nextAction.type=="open_iframe_modal"){
          return;
        }
        if(nextAction.type=="execute_response_callback"){
          let responseData=JSON.parse(JSON.stringify(nextAction.data));
          if("url" in responseData)
            delete responseData.url;
          if("image" in responseData)
            delete responseData.image;
          responseCallback(responseData);
          return;
        }
        if(nextAction.type=="reset_payment_method"){
          if(!configSettings.display_result_screen){
            let responseData=JSON.parse(JSON.stringify(nextAction.data));
            if("url" in responseData)
              delete responseData.url;
            if("image" in responseData)
              delete responseData.image;
            responseCallback(responseData);
          }else{
            chooseMethod(selectedMethod, operation);
          }
          return;
        }
      }
      executePaymentMethod({
        operation,
        paymentMethod: paymentMethodToSubmit,
        integrations,
        locale: language
      });
      if(paymentMethodToSubmit.code=="CUOTEALO" || paymentMethodToSubmit.code=="BANK_TRANSFER"){
        popupRef.current = window.open('', '_blank');
        window.flex_pop_up_poqwieurndf30_ref = popupRef.current;
        OccurrenceRegistry.registerOccurrence("cuotealo");
        try {
          let result=await OccurrenceRegistry.waitForOccurrence("cuotealo");
          popupRef.current.location.href = result.data;
        } catch (err) {
          console.error("fallo window.open");
          popupRef.current.close();
        }
      }
    }

    if (!selectedMethod) return null;
    return (
        <div className={"payment-actions"}>
          {/* DelayClock montado siempre (fuera del ternario) para no desmontarse al cambiar de estado. Mismas props. */}
          <DelayClock onEndOfDelay={onEndOfDelay} delay={delay} onStartOfDelay={onStartOfDelay} onDelayTick={onDelayTick} skipDelay={skipDelay}/>
          {showResult ? (
            <>
              <div className="payment-actions-redirect-label">{tMixed(i18nPaymentBtnText || paymentBtnText)} {timeLeft} s</div>
              <div className={"payment-actions-progress"}>
                <div className={"payment-actions-progress-bar"} style={{width: progress + '%'}} />
              </div>
              <button type="button" className={"payment-result-return-btn"} onClick={onReturnNow}>{tMixed(i18nBackBtnText || backBtnText)}</button>
            </>
          ) : (
            <>
              {/* El contenido de la vista de cuotas (step installments) lo pinta PaymentForm (zona de contenido),
                  no aqui. PaymentActions solo controla el boton de la barra inferior segun el step. */}
              <div className={"payment-actions-amount"}>
                <div className={"amount-label"}>{t('common.pay_full')}</div>
                <div className={"amount-value"}><span className={"currency"}>{getCurrencySymbolSafe(currencyCode)}</span><span className={"integer-part"}>{formatAmountPart(amountMinor,2,"integer")}</span><span className={"decimal-part"}>.{formatAmountPart(amountMinor,2,"decimal")}</span></div>
              </div>
              {/* Google Pay usa el boton nativo SOLO en su primera vista (sin respuesta de pago
                  pendiente). Tras una respuesta (denegada/error = reset_payment_method, o aprobada =
                  execute_response_callback) debe comportarse como el resto de metodos: boton normal
                  que ejecuta onSubmit() (p.ej. "Elegir otro metodo"). */}
              {selectedMethod.code === "GOOGLE_PAY" && isFirstView ? (
                <div className={"payment-actions-submit"}>
                  {/* Barra inferior: paso 1 boton nativo (abre popup); paso 2 "Confirmar pago" (paga con cuotas). */}
                  {googlePay.step === "installments" ? (
                    <button type="button" className={"flex-submit-button"} onClick={() => googlePay.confirmInstallments()}>
                      {tBackend(googlePay.installmentsI18n && googlePay.installmentsI18n.confirm)}
                    </button>
                  ) : googlePay.step === "loading" ? (
                    // En loading NO mostramos spinner en el boton (el feedback vive en el
                    // contenido del checkout). El boton nativo se oculta para evitar reintentos.
                    <div className={"google-pay-button-wrapper"} aria-hidden="true" />
                  ) : googlePay.step === "unavailable" ? (
                    // SDK no disponible: el aviso lo muestra PaymentForm (zona de contenido).
                    // La barra inferior no monta el boton nativo para no re-disparar la deteccion.
                    null
                  ) : (
                    <GooglePayButton onToken={googlePay.onToken} />
                  )}
                </div>
              ) : (
                <div className={"payment-actions-submit"+((isFirstView && !isFormValid)?" disabled":"")}>
                  <button type="button" className={"flex-submit-button"} onClick={e=>{onSubmit()}}>{isTicking ? tMixed(i18nPaymentBtnText || paymentBtnText) + " " + t('common.in')+" " + timeLeft + " " + t('common.secs')+" " : tMixed(i18nPaymentBtnText || paymentBtnText)}</button>
                </div>
              )}
            </>
          )}
        </div>
    )
}
